import { NUMBER_FORMAT_LOCALE } from "@/global/_constants";
import jwt_decode from "jwt-decode";
import CryptoJS from "crypto-js";
import Papa from "papaparse";
import vuetify from "../plugins/vuetify";

export function createCSVBlob(csv) {
  return new Blob(["\uFEFF", csv], { type: "text/csv;charset=utf-8;" });
}

// Shared by CloudFilePicker.vue (tree rows) and UploadSourceSelector.vue (chips)
// so a file's icon/color agree wherever it's shown.
export function getCloudFileIcon(mimeType) {
  if (!mimeType) return "mdi-file-outline";
  if (mimeType.includes("pdf")) return "mdi-file-pdf-box";
  if (
    mimeType.includes("spreadsheet") ||
    mimeType.includes("excel") ||
    mimeType.includes("csv")
  )
    return "mdi-file-excel";
  if (mimeType.includes("presentation") || mimeType.includes("powerpoint"))
    return "mdi-file-powerpoint";
  if (mimeType.includes("word") || mimeType.includes("document"))
    return "mdi-file-word";
  if (mimeType.includes("image")) return "mdi-file-image";
  if (mimeType.includes("json")) return "mdi-code-json";
  return "mdi-file-outline";
}

export function getCloudFileColor(mimeType) {
  if (!mimeType) return "blue-grey";
  if (mimeType.includes("pdf")) return "red darken-1";
  if (
    mimeType.includes("spreadsheet") ||
    mimeType.includes("excel") ||
    mimeType.includes("csv")
  )
    return "green darken-1";
  if (mimeType.includes("presentation") || mimeType.includes("powerpoint"))
    return "orange darken-1";
  if (mimeType.includes("word") || mimeType.includes("document"))
    return "blue darken-1";
  if (mimeType.includes("image")) return "purple darken-1";
  return "blue-grey";
}

export function authorize(auth = []) {
  const currentUser = getCurrentUser();
  if (!currentUser) return false; // Handle the null case gracefully
  let parsedJWTJson = jwt_decode(currentUser.access_token);
  const scope = parsedJWTJson["scope"];
  const intersection = scope.filter(value => auth.includes(value));
  return !(auth.length && intersection.length === 0);
}
export function parsedAccessToken() {
  const currentUser = getCurrentUser();
  if (!currentUser) return false; // Handle the null case gracefully

  return jwt_decode(currentUser.access_token);
}

export function isLoggedInUser(userId) {
  const currentUser = getCurrentUser();
  if (!currentUser) return false; // Handle the null case gracefully
  let parsedJWTJson = jwt_decode(currentUser.access_token);
  return parsedJWTJson["sub"] === userId;
}

// used for project level access control
export function hasProjectAccessFor(roles = [], projectRole) {
  if (!projectRole && this.$store.state.project.project_role) {
    projectRole = this.$store.state.project.project_role;
  }

  return [...roles].includes(projectRole);
}

export function timeToNextToken() {
  let user = getCurrentUser();
  let now = new Date();
  let expiresAt = new Date(user.expires_at);
  return (expiresAt - now) / 1000;
}

export function timeToNextWalletToken() {
  let wallet = JSON.parse(localStorage.getItem("wallet_user"));
  let now = new Date();
  let diff = 0;
  let wallet_expire = wallet.expires_at;
  if (wallet && wallet_expire) {
    let expiresAt = new Date(wallet_expire);
    diff = (expiresAt - now) / 1000;
  }
  return diff;
}

export function getCurrentUser() {
  const user = localStorage.getItem("doozie_user");
  return user ? JSON.parse(user) : null;
}

export function setCurrentUser(user) {
  localStorage.setItem("doozie_user", JSON.stringify(user));
}

export function deleteCurrentUser() {
  localStorage.removeItem("doozie_user");
}

export function deleteCurrentWalletUser() {
  localStorage.removeItem("wallet_account");
  localStorage.removeItem("wallet_user");
}

export function capitalizeFirstLetter(string) {
  return string.charAt(0).toUpperCase() + string.slice(1);
}

export function capitalizeWords(string) {
  return string.replace(/(?:^|\s)\S/g, function (a) {
    return a.toUpperCase();
  });
}

export function numberToPercentage(num) {
  return num.toFixed(2) + "%";
}

export function humanReadableKey(key) {
  let value = key.replace(/_/g, " ");
  return value.charAt(0).toUpperCase() + value.slice(1);
}

export function removeUnderscoreFromString(string) {
  return string.replace(/_/g, " ");
}

export async function copyTextToClipboard(text) {
  if (!navigator.clipboard) return false;
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch (error) {
    return false;
  }
}

export function formatNum(value) {
  return new Intl.NumberFormat(NUMBER_FORMAT_LOCALE, undefined).format(value);
}

export function getUniqueArrayOfObjects(data = [], property) {
  const uniqueMap = {};

  return data.reduce((acc, current) => {
    if (!uniqueMap[current[property]]) {
      uniqueMap[current[property]] = true;
      acc.push(current);
    }
    return acc;
  }, []);
}
export function formatDateTime(datetime, format, date_only) {
  var date = new Date(datetime.toLocaleString());
  var hours = date.getHours();
  var minutes = date.getMinutes();
  var seconds = date.getSeconds();
  var months = date.getMonth() + 1;
  months = months < 10 ? "0" + months : months;
  var dates = date.getDate();
  dates = dates < 10 ? "0" + dates : dates;

  if (date_only) {
    return date.getFullYear() + "-" + months + "-" + dates;
  }

  var strTime;
  if (format === "24") {
    hours = hours % 24;
    hours = hours ? hours : 24; // the hour '0' should be '12'
    minutes = minutes < 10 ? "0" + minutes : minutes;
    seconds = seconds < 10 ? "0" + seconds : seconds;
    strTime = hours + ":" + minutes + ":" + seconds;
  } else {
    var ampm = hours >= 12 ? "PM" : "AM";
    hours = hours % 12;
    hours = hours ? hours : 12; // the hour '0' should be '12'
    minutes = minutes < 10 ? "0" + minutes : minutes;
    seconds = seconds < 10 ? "0" + seconds : seconds;
    strTime = hours + ":" + minutes + ":" + seconds + " " + ampm;
  }
  return date.getFullYear() + "-" + months + "-" + dates + " at " + strTime;
}

export function dateTimeToStr(date, format) {
  var z = {
    M: date.getMonth() + 1,
    d: date.getDate(),
    h: date.getHours(),
    m: date.getMinutes(),
    s: date.getSeconds(),
  };
  format = format.replace(/(M+|d+|h+|m+|s+)/g, function (v) {
    return ((v.length > 1 ? "0" : "") + z[v.slice(-1)]).slice(-2);
  });

  return format.replace(/(y+)/g, function (v) {
    return date.getFullYear().toString().slice(-v.length);
  });
}

export function dateOfToday() {
  return dateTimeToStr(new Date(), "yyyy-MM-dd");
}

export function getLocale() {
  return localStorage.getItem("language")?.substring(0, localStorage.getItem("language")?.indexOf("-")) || "en";
}
export function getSubscriptionTypeColor(subscription_type) {
  return subscription_type === "paid" ? "success" : "warning";
}

function toUrlSafeBase64(base64String) {
  return base64String.replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

function fromUrlSafeBase64(urlSafeString) {
  let base64String = urlSafeString.replace(/-/g, "+").replace(/_/g, "/");
  while (base64String.length % 4) {
    base64String += "=";
  }
  return base64String;
}

export function encryptData(data, secretKey) {
  const jsonData = JSON.stringify(data);
  const encryptedData = CryptoJS.AES.encrypt(jsonData, secretKey).toString();
  return toUrlSafeBase64(encryptedData);
}

export function decryptData(encryptedData, secretKey) {
  const base64EncryptedData = fromUrlSafeBase64(encryptedData);
  const bytes = CryptoJS.AES.decrypt(base64EncryptedData, secretKey);

  return JSON.parse(bytes.toString(CryptoJS.enc.Utf8));
}

export function isValidHhMmFormat(hhMmStr) {
  if (!hhMmStr) return false;
  let validHhMmRegex = /^([0-1]?\d|2[0-3]):([0-5]\d)$/;
  return validHhMmRegex.test(hhMmStr);
}

export function hhMmToMinutes(hhMmStr) {
  if (!isValidHhMmFormat(hhMmStr)) return null;
  const [hours, minutes] = hhMmStr.split(":").map(Number);
  return hours * 60 + minutes;
}

export function timeAgo(ts) {
  if (!ts) return "";

  const t = new Date(ts).getTime();
  if (isNaN(t)) return "";

  let diff = Date.now() - t;

  if (diff < 0) diff = 0;

  const minutes = Math.floor(diff / 60000);
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes}m ago`;

  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;

  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;

  const weeks = Math.floor(days / 7);
  if (weeks < 4) return `${weeks}w ago`;

  const months = Math.floor(days / 30);
  if (months < 12) return `${months}mo ago`;

  const years = Math.floor(days / 365);
  return `${years}y ago`;
}

export function getLoggedInUserId() {
  const currentUser = getCurrentUser();
  if (!currentUser) return false;
  let parsedJWTAccessToken = jwt_decode(currentUser.access_token);
  return parsedJWTAccessToken?.sub;
}

export async function checkMandatoryColumns(file, mandatoryColumns = ["word", "definition", "synonyms"]) {
  const results = await new Promise((resolve, reject) => {
    Papa.parse(file, {
      header: true,
      complete: resolve,
      error: reject,
    });
  });

  const headers = (results.meta?.fields || []).map(h => h.trim().toLowerCase());
  const missingColumns = mandatoryColumns.filter(c => !headers.includes(c));

  if (missingColumns.length > 0) {
    const missingColumnsText = missingColumns.join(", ");
    const baseMessage = vuetify.framework.lang.t("$vuetify.gpt_dataset.dictionary.missing_columns_error");
    const message = baseMessage.replace(/\{columns}/g, missingColumnsText);
    throw new Error(message);
  }

  return true;
}

// used for project level user role access control
function getProjectFeatureAccess(project) {
  const projectRole = project.project_role;
  const features = project.selectedProject?.permissions?.features;
  return {
    features,
    isNonUserRole: Boolean(projectRole && projectRole !== "user"),
  };
}

export function isFeatureEnabled(featureKey) {
  const { features, isNonUserRole } = getProjectFeatureAccess(this.$store.state.project);

  if (isNonUserRole || !features) return true;

  return features?.[featureKey]?.enabled ?? true;
}

export function isSectionEnabled(featureKey, sectionKey) {
  const { features, isNonUserRole } = getProjectFeatureAccess(this.$store.state.project);

  if (isNonUserRole || !features) return true;

  const feature = features?.[featureKey];
  if (!feature) {
    return true;
  }

  if (!feature.enabled) {
    return false;
  }

  return feature.sections?.[sectionKey]?.enabled ?? true;
}

export function extractFeaturePermissions(features = {}) {
  return Object.entries(features).reduce((result, [featureKey, feature]) => {
    result[featureKey] = {
      enabled: feature.enabled,
      sections: Object.entries(feature.sections || {}).reduce((sections, [sectionKey, section]) => {
        sections[sectionKey] = {
          enabled: section.enabled,
        };
        return sections;
      }, {}),
    };

    return result;
  }, {});
}
export function mergeFeatureConfig(userFeatures = {}, defaultFeatures = {}) {
  userFeatures = userFeatures || {};

  const merged = {};

  Object.entries(defaultFeatures).forEach(([featureKey, defaultFeature]) => {
    const userFeature = userFeatures[featureKey] || {};

    merged[featureKey] = {
      ...defaultFeature,
      enabled: userFeature.enabled ?? defaultFeature.enabled,
      sections: {},
    };

    Object.entries(defaultFeature.sections || {}).forEach(([sectionKey, defaultSection]) => {
      const userSection = userFeature.sections?.[sectionKey] || {};

      merged[featureKey].sections[sectionKey] = {
        ...defaultSection,
        enabled: userSection.enabled ?? defaultSection.enabled,
      };
    });
  });

  return merged;
}

export function hasEnabledSection(featureKey) {
  const { features, isNonUserRole } = getProjectFeatureAccess(this.$store.state.project);

  if (isNonUserRole || !features) {
    return true;
  }

  const feature = features?.[featureKey];

  if (!feature) {
    return true;
  }

  if (!feature.enabled) {
    return false;
  }

  const sections = feature.sections || {};

  // No sections means the feature itself is accessible
  if (Object.keys(sections).length === 0) {
    return true;
  }

  return Object.values(sections).some(section => section.enabled);
}

export function getNoTabPermissionMessage() {
  const { features, isNonUserRole } = getProjectFeatureAccess(this.$store.state.project);
  if (isNonUserRole || !features) {
    return "";
  }
  if (Object.keys(features).length === 0) {
    return "";
  }

  const hasAccessibleFeature = Object.keys(features).some(featureKey => hasEnabledSection.call(this, featureKey));
  return hasAccessibleFeature
    ? ""
    : vuetify.framework.lang.t("$vuetify.user_access_control_permission_feature.no_access_message");
}

// PDF, PPT/PPTX and DOCX share the is_ocr/is_text_extraction/metadata group on the backend.
export function isTextExtractableFile(file) {
  const type = String(file?.type || "").toLowerCase();
  const name = String(file?.name || "").toLowerCase();
  return (
    type.includes("pdf") ||
    type.includes("presentation") ||
    type.includes("powerpoint") ||
    type.includes("wordprocessingml") ||
    /\.(pdf|ppt|pptx|docx)$/.test(name)
  );
}

export function removeOcrMetadata(metadata) {
  if (!metadata) return metadata;

  try {
    const parsedValue = typeof metadata === "string" ? JSON.parse(metadata) : metadata;

    if (!parsedValue || typeof parsedValue !== "object" || Array.isArray(parsedValue)) {
      return metadata;
    }

    const parsedMetadata = { ...parsedValue };
    delete parsedMetadata.instruction;
    delete parsedMetadata.max_tokens;
    delete parsedMetadata.language;

    return typeof metadata === "string" ? JSON.stringify(parsedMetadata) : parsedMetadata;
  } catch {
    return metadata;
  }
}
